import React from "react";

/**
 * Crowther · icon set (Part 4.4 master brief).
 *
 * 12 icons at a 24×24 grid, 1.5 px stroke, single `currentColor` path.
 * No fills. No text. Consumers apply size via prop or CSS.
 *
 * Naming follows the master brief slots:
 *   Shield / sovereignty                → IconShield
 *   Server rack / infrastructure        → IconServer
 *   Lock / deny by default              → IconLock
 *   Compass / deployment                → IconCompass
 *   Globe / regions                     → IconGlobe
 *   Ledger / audit trail                → IconLedger
 *   Stack / one platform                → IconStack
 *   Gantry (machines class 1)           → IconGantry
 *   Carrier (machines class 2)          → IconCarrier
 *   Arm (machines class 3)              → IconArm
 *   Network / nodes                     → IconNetwork
 *   Chart / analytics                   → IconChart
 */

const base = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

function withSize(size) {
  return size ? { width: size, height: size } : {};
}

export function IconShield({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="shield">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path d="M9.5 12.5l2 2 3.5-4" />
    </svg>
  );
}

export function IconServer({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="server">
      <rect x="4" y="4" width="16" height="6" rx="1" />
      <rect x="4" y="14" width="16" height="6" rx="1" />
      <circle cx="7.5" cy="7" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="17" r="0.6" fill="currentColor" stroke="none" />
      <path d="M11 7h6M11 17h6" />
    </svg>
  );
}

export function IconLock({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="lock">
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 018 0v3" />
      <path d="M12 15v2" />
    </svg>
  );
}

export function IconCompass({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="compass">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15.5 8.5L13 13l-4.5 2.5L11 11l4.5-2.5z" />
    </svg>
  );
}

export function IconGlobe({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="globe">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.5 2.6 3.8 5.6 3.8 8.5s-1.3 5.9-3.8 8.5c-2.5-2.6-3.8-5.6-3.8-8.5s1.3-5.9 3.8-8.5z" />
    </svg>
  );
}

export function IconLedger({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="ledger">
      <rect x="5" y="3.5" width="14" height="17" rx="1.5" />
      <path d="M8.5 8h7M8.5 12h7M8.5 16h4" />
    </svg>
  );
}

export function IconStack({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="stack">
      <path d="M12 3.5l8.5 4-8.5 4-8.5-4 8.5-4z" />
      <path d="M3.5 12l8.5 4 8.5-4" />
      <path d="M3.5 16.5l8.5 4 8.5-4" />
    </svg>
  );
}

export function IconGantry({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="gantry">
      <path d="M4 8h16" />
      <path d="M6 8v10M18 8v10" />
      <path d="M4 20h16" />
      <path d="M12 8v5" />
      <rect x="10.5" y="13" width="3" height="3" />
    </svg>
  );
}

export function IconCarrier({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="carrier">
      <rect x="3.5" y="8" width="17" height="7" rx="1" />
      <circle cx="7.5" cy="17" r="1.75" />
      <circle cx="16.5" cy="17" r="1.75" />
    </svg>
  );
}

export function IconArm({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="arm">
      <rect x="8" y="19" width="8" height="1.5" rx="0.5" />
      <path d="M12 19v-4" />
      <circle cx="12" cy="14.5" r="1.25" />
      <path d="M12 13.5l5-3.5" />
      <circle cx="17" cy="10" r="1.25" />
      <path d="M17 8.9V5.5" />
    </svg>
  );
}

export function IconNetwork({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="network">
      <circle cx="5" cy="6" r="1.75" />
      <circle cx="19" cy="6" r="1.75" />
      <circle cx="12" cy="12" r="1.75" />
      <circle cx="5" cy="18" r="1.75" />
      <circle cx="19" cy="18" r="1.75" />
      <path d="M6.4 7.1L10.7 10.9M17.6 7.1L13.3 10.9M6.4 16.9L10.7 13.1M17.6 16.9L13.3 13.1" />
    </svg>
  );
}

export function IconChart({ size, ...rest }) {
  return (
    <svg {...base} {...withSize(size)} {...rest} data-icon="chart">
      <path d="M4 20h16" />
      <path d="M4 20V4" />
      <path d="M8 16v-4M12 16V9M16 16v-6M20 16V6" />
    </svg>
  );
}

/** Convenience lookup used by consumers that render a chosen icon by name. */
export const ICONS = {
  shield: IconShield,
  server: IconServer,
  lock: IconLock,
  compass: IconCompass,
  globe: IconGlobe,
  ledger: IconLedger,
  stack: IconStack,
  gantry: IconGantry,
  carrier: IconCarrier,
  arm: IconArm,
  network: IconNetwork,
  chart: IconChart,
};

export function Icon({ name, size, ...rest }) {
  const Cmp = ICONS[name];
  return Cmp ? <Cmp size={size} {...rest} /> : null;
}
